import { Form, Input, Modal, Select, message } from "antd";
import { COUNTRIES } from "common/constants";
import { useState } from "react";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import {
  useCreateLawFirmMutation,
  useUpdateLawFirmMutation,
} from "features/lawFirmsTable/lawFirmsAPI";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

const getPostalCodeMaxLength = (country) => {
  if (country === "US") return 5;
  if (country === "CA") return 7;
  return undefined;
};

export default function LawFirmModal({ title, onOk, onCancel, selectedLawFirm }) {
  const [form] = Form.useForm();
  const { data: constants } = useFetchBackendConstantsQuery();
  const [addLawFirm] = useCreateLawFirmMutation();
  const [updateLawFirm] = useUpdateLawFirmMutation();

  const [country, setCountry] = useState(selectedLawFirm?.country ?? "US");

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const onCountryChange = (value) => {
    setCountry(value);
    form.setFieldsValue({ state: undefined, zipCode: undefined });
  };

  const onSubmit = async () => {
    const fields = await form.validateFields();
    const action = selectedLawFirm
      ? updateLawFirm({ ...fields, lawFirmId: fields.id })
      : addLawFirm({ ...fields });
    const result = await action;

    if ("data" in result) {
      await form.resetFields();
      onOk();
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
      message.error(`Failed to save law firm. Please try again later.`);
    }
  };

  return (
    <Modal
      maskClosable={false}
      title={title}
      okText="Submit"
      open
      onOk={onSubmit}
      onCancel={onCancel}
    >
      <StyledForm
        form={form}
        layout="vertical"
        validateMessages={{ required: "This is a required field" }}
        initialValues={{ country: "US", ...selectedLawFirm }}
      >
        <Form.Item name="id" hidden>
          <Input hidden />
        </Form.Item>
        <Form.Item name="code" label="Law Firm Code" rules={[{ required: true }]}>
          <Input placeholder="Enter Law Firm Code..." />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="Enter Name..." />
        </Form.Item>
        <Form.Item name="address1" label="Address Line 1" rules={[{ required: true }]}>
          <Input placeholder="Enter Address..." />
        </Form.Item>
        <Form.Item name="address2" label="Address Line 2">
          <Input placeholder="Enter Address..." />
        </Form.Item>
        <Form.Item
          name="city"
          label="City"
          rules={[
            { pattern: /^[^\d]*$/, message: "Please enter a valid city" },
            { required: true },
          ]}
        >
          <Input placeholder="Enter City..." />
        </Form.Item>
        <Form.Item name="country" label="Country" rules={[{ required: true }]}>
          <Select
            placeholder="Select a country..."
            showSearch
            filterOption={filterOption}
            options={COUNTRIES}
            onChange={onCountryChange}
          />
        </Form.Item>
        <Form.Item name="state" label="State/Province" rules={[{ required: true }]}>
          {country === "US" || country === "CA" ? (
            <Select
              placeholder="Select one..."
              options={(country === "CA" ? constants?.canadianProvinces : constants?.states)?.map(
                (state) => ({
                  label: `${state.display} (${state.value})`,
                  value: state.value,
                }),
              )}
              filterOption={filterOption}
              showSearch
            />
          ) : (
            <Input placeholder="Enter State/Province..." />
          )}
        </Form.Item>
        <Form.Item
          name="zipCode"
          label="Zip/Postal Code"
          dependencies={["country"]}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const selectedCountry = getFieldValue("country");
                if (selectedCountry !== "US") {
                  return Promise.resolve();
                }
                if (!value || /^\d{5}(-\d{4})?$/.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Must be a valid zip code"));
              },
            }),
          ]}
        >
          <Input
            placeholder={country === "CA" ? "A1A 1A1" : "Enter Zip/Postal Code..."}
            maxLength={getPostalCodeMaxLength(country)}
          />
        </Form.Item>
        <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
          <Input placeholder="XXX-XXX-XXXX" />
        </Form.Item>
        <Form.Item name="email" label="Email">
          <Input placeholder="Enter Email..." />
        </Form.Item>
        <Form.Item name="notes" label="Notes / Description">
          <Input placeholder="Enter Notes..." />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}
